import React, { useState, Fragment, ChangeEvent, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { themes, ProfileImageError } from 'kb-shared';
import { useTranslation } from 'react-i18next';
import Honeybadger from '@honeybadger-io/js';

import * as redux from '../../../redux';
import { ReduxState } from '../../../redux';
import {
  isValidProfileImageExtension,
  showErrorProfileImageExtensionToast
} from '../../../utilities/fileValidation';
import WhiteSquare from '../../WhiteSquare';
import { FileUpload, FileUploadContainer } from '../lib/styled';
import ProfileImage from './components/ProfileImage';
import ProfilePhotoUploadModal from './ProfilePhotoUploadModal';
import { retrieveFile, CATEGORY } from '../../../api/pf2/files';

const FileUploadDescription = styled.small`
  ${themes.fonts.FoundersGrotesk};
  max-width: 140px;
  display: inline-block;
  text-align: center;
  margin: 8px 0 0 0;
`;

const {
  patient: { updateProfileImage, profileImageError }
} = redux;

type Props = {
  mode: 'placeholder' | 'whiteSquare';
  size?: number;
  profileImage?: string;
  profileImageError: ProfileImageError | null;
  updateProfileImage: (url: string) => void;
  profileImageDidNotLoad: () => void;
  className?: string;
};

const ProfileImageUploadButton = (props: Props) => {
  const [croppingPhoto, setCroppingPhoto] = useState<boolean>(false);
  const [currentFileBlob, setCurrentFileBlob] = useState<string | undefined>();
  const { t } = useTranslation();
  const { size, className } = props;
  const body = document.body;

  const getUserImage = async () => {
    const { updateProfileImage, profileImageDidNotLoad } = props;

    try {
      const { file } = await retrieveFile(CATEGORY.PROFILE_PHOTO);
      updateProfileImage(file);
    } catch (error) {
      Honeybadger.notify(error);
      profileImageDidNotLoad();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const {
      target: { files }
    } = e;

    if (!files || !files[0]) {
      return;
    }

    if (!isValidProfileImageExtension(files[0].type)) {
      return showErrorProfileImageExtensionToast();
    }

    setCurrentFileBlob(URL.createObjectURL(files[0]));
    setCroppingPhoto(true);
  };

  const onUserImageError = () => {
    props.profileImageDidNotLoad();
  };

  const renderButton = () => {
    const { mode, profileImage, size, profileImageError } = props;

    switch (mode) {
      case 'placeholder':
        return (
          <ProfileImage
            profileImage={profileImage}
            error={profileImageError}
            size={size}
            onError={onUserImageError}
          />
        );
      case 'whiteSquare':
        return (
          <>
            <WhiteSquare />
            <FileUploadDescription>
              {t('upload_button.help_text', { megabytes: 2 })}
            </FileUploadDescription>
          </>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    getUserImage();
  }, []);

  if (!body) return null;

  return (
    <Fragment>
      <FileUploadContainer
        // @ts-ignore
        onClick={() => setCroppingPhoto(true)}
        size={size}
        className={className}
      >
        <FileUpload
          type="file"
          onChange={handleFileChange}
          accept="image/png, image/jpg, image/jpeg"
          size={size}
        />
        {renderButton()}
      </FileUploadContainer>

      {croppingPhoto &&
        ReactDOM.createPortal(
          <ProfilePhotoUploadModal
            // @ts-ignore
            currentFileBlob={currentFileBlob}
            onSuccess={() => getUserImage()}
            onClose={() => setCroppingPhoto(false)}
          />,
          body
        )}
    </Fragment>
  );
};

export default connect(
  ({ patient }: ReduxState) => ({
    profileImage: patient.patient ? patient.patient.profileImage : undefined,
    profileImageError: patient.profileImageError
  }),
  dispatch => ({
    updateProfileImage: (url?: string) => dispatch(updateProfileImage(url)),
    profileImageDidNotLoad: () => dispatch(profileImageError({ type: 'UnableToLoadImage' }))
  })
)(ProfileImageUploadButton);
